import React from "react";
import classnames from "classnames";
import { Classes, InputGroup, InputGroupProps, TextArea, TextAreaProps } from "@blueprintjs/core";
import styles from "./Input.module.scss";

const variantClass = {
    default: "planr-default-input",
    tiny: "planr-tiny-input",
    round: "planr-round-input",
    // без темы: поле целиком оформлено своим CSS-модулем (ячейки таблиц, фильтры)
    plain: undefined,
};

export type PlanrInputVariant = keyof typeof variantClass;

/**
 * Текстовое поле planr — Blueprint `InputGroup` с уже навешенным классом темы.
 * Раньше класс `planr-default-input` вешали руками, и забытый класс молча
 * давал поле другого вида.
 *
 * Автозаполнение браузера выключено: его выключали во всех полях приложения
 * (`autoComplete="off"` стоял в каждом), иначе подсказки браузера закрывают
 * соседние поля формы. Включается пропом.
 *
 * Все пропсы `InputGroup` пробрасываются как есть, так что поле подходит
 * и под `{...field}` Formik-а.
 */
export const PlanrInput = ({ variant = "default", className, ...props }: PlanrInputProps) => (
    <InputGroup autoComplete="off" {...props} className={classnames(styles.input, variantClass[variant], className)} />
);

export interface PlanrInputProps extends InputGroupProps {
    variant?: PlanrInputVariant;
}

/**
 * Многострочное поле planr. По умолчанию растёт по высоте вслед за текстом
 * (`autoResize` Blueprint 6 — пересчитывает высоту при монтировании и при каждом
 * изменении значения) и занимает всю ширину.
 *
 * Разметка та же, что собиралась вручную: `TextArea` внутри обёртки
 * `bp6-input-group planr-default-input`. `className` уходит на обёртку,
 * `textAreaClassName` — на само поле. В `variant="plain"` обёртки нет,
 * и `className` уходит на поле.
 */
export const PlanrTextArea = ({
    variant = "default",
    className,
    textAreaClassName,
    autoResize = true,
    fill = true,
    ...props
}: PlanrTextAreaProps) =>
    variant === "plain" ? (
        <TextArea
            autoComplete="off"
            {...props}
            autoResize={autoResize}
            fill={fill}
            className={classnames(className, textAreaClassName)}
        />
    ) : (
        <div className={classnames(Classes.INPUT_GROUP, variantClass.default, className)}>
            <TextArea
                autoComplete="off"
                {...props}
                autoResize={autoResize}
                fill={fill}
                className={textAreaClassName}
            />
        </div>
    );

export interface PlanrTextAreaProps extends TextAreaProps {
    variant?: "default" | "plain";
    /** Класс самого поля; `className` в варианте `default` уходит на обёртку. */
    textAreaClassName?: string;
}
